import { MatrizEvidencia } from "../../shared/model/iso42001/matriz-riesgo.model";

/**
 * Formatos que comparten el Expediente del activo y el Expediente del riesgo,
 * en pantalla y en el PDF. Son funciones puras: no dependen del componente.
 */

/** Un cruce de la verificación de consistencia del expediente. */
export interface Verificacion {
  ok: boolean;
  texto: string;
}

const KILO = 1024;

/** El tamaño de una evidencia: "512 B", "3.4 KB" o "1.2 MB". */
export function tamanoLegible(bytes?: number): string {
  if (bytes === null || bytes === undefined) {
    return "";
  }
  if (bytes < KILO) {
    return bytes + " B";
  }
  const enKilos = bytes < KILO * KILO;
  return (bytes / (enKilos ? KILO : KILO * KILO)).toFixed(1) + (enKilos ? " KB" : " MB");
}

const EXTENSIONES_HOJA = [".xls", ".xlsx", ".csv"];

/** El icono de una evidencia, por su tipo o por la extensión del archivo. */
export function iconoEvidencia(evd: MatrizEvidencia): string {
  if (evd.tipoArchivo === "IMAGEN") {
    return "🖼";
  }
  if (evd.tipoArchivo === "VIDEO") {
    return "🎬";
  }
  const archivo = (evd.nombreArchivo || "").toLowerCase();
  if (archivo.endsWith(".pdf")) {
    return "📄";
  }
  return EXTENSIONES_HOJA.some((ext) => archivo.endsWith(ext)) ? "📊" : "📎";
}

const MES_EN: Readonly<Record<string, string>> = {
  Jan: "01", Feb: "02", Mar: "03", Apr: "04", May: "05", Jun: "06",
  Jul: "07", Aug: "08", Sep: "09", Oct: "10", Nov: "11", Dec: "12",
};
const FECHA_JAVA = /^[A-Z][a-z]{2} ([A-Z][a-z]{2}) (\d{1,2}) \d{2}:\d{2}:\d{2} \S+ (\d{4})$/;
const FECHA_ISO = /^(\d{4})-(\d{2})-(\d{2})(?:[T ].*)?$/;

/**
 * Algunos valores viejos de bitácora quedaron como el toString de Java
 * ("Wed May 27 00:00:00 CST 2026") o como fecha ISO. Aquí se vuelven
 * dd/mm/aaaa; cualquier otro texto pasa igual.
 */
export function valorLegible(v?: string): string {
  if (!v) {
    return "";
  }
  const java = FECHA_JAVA.exec(v);
  const mes = java ? MES_EN[java[1]] : undefined;
  if (java && mes) {
    return `${java[2].padStart(2, "0")}/${mes}/${java[3]}`;
  }
  const iso = FECHA_ISO.exec(v);
  return iso ? `${iso[3]}/${iso[2]}/${iso[1]}` : v;
}

const FECHA_CORTA: Intl.DateTimeFormatOptions = { day: "2-digit", month: "short", year: "numeric" };
const FECHA_CON_HORA: Intl.DateTimeFormatOptions = { ...FECHA_CORTA, hour: "2-digit", minute: "2-digit" };
const SOLO_DIA = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * Una fecha para el PDF ("12 ago 2026", con la hora si se pide). Un
 * "yyyy-MM-dd" pelón se arma como fecha LOCAL: new Date("2026-08-12") sería
 * medianoche UTC y en México imprimiría el día anterior, y el PDF
 * contradiría la pantalla.
 */
export function fechaPdf(v?: string | Date | null, conHora = false): string {
  if (!v) {
    return "—";
  }
  const soloDia = typeof v === "string" ? SOLO_DIA.exec(v) : null;
  if (soloDia) {
    const local = new Date(Number(soloDia[1]), Number(soloDia[2]) - 1, Number(soloDia[3]));
    return local.toLocaleString("es-MX", FECHA_CORTA);
  }
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) {
    return String(v);
  }
  return d.toLocaleString("es-MX", conHora ? FECHA_CON_HORA : FECHA_CORTA);
}
